import { RotatingRole, usePrefersReducedMotion } from "@components/animations";
import { DotedBox, RectangleShape } from "@components/shape";
import { bannerData } from "@demo-data/banner";
import { layout } from "@demo-data/layout";
import { Grid, Stack, Typography } from "@mui/material";
import { Box, useTheme } from "@mui/system";
import { addScriptDefault } from "meta-pixel";
import { CSSProperties, Fragment, useRef } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { Link } from "react-router-dom";
import styles from "./banner.module.css";

/** Rotating role titles shown under the hero heading. */
const HERO_ROLES = [
  "scalable web apps",
  "delightful interfaces",
  "design systems",
  "performant products",
] as const;

/** Split a phrase into individual animated words. */
const toWords = (text: string, accent: boolean) =>
  text
    .split(" ")
    .filter(Boolean)
    .map((word) => ({ text: word, accent }));

/** Small helper for per-element entrance delays. */
const delay = (ms: number): CSSProperties =>
  ({ "--anim-delay": `${ms}ms` } as CSSProperties);

export default function Banner() {
  const theme = useTheme();
  const {
    title: { title1, title2, title3, title4, title5, title6 },
    subtitle,
    doing: { text, url, urlText, target },
    blockquote: { quoteText1, quoteText2, quoteText3, quoteText4, quoteBy },
    image,
  } = bannerData || {};

  // Hero heading split into individual words for the staggered reveal.
  const headingWords = [
    ...toWords(title1, false),
    ...toWords(title2, false),
    ...toWords(title3, true),
    ...toWords(title4, false),
    ...toWords(title5, true),
    ...toWords(title6, true),
  ];

  // Subtle pointer parallax for the hero visual. The shapes counter-move at a
  // larger rate than the portrait, which reads as real depth. Transform-only,
  // written inside rAF, so no React re-renders.
  const reduceMotion = usePrefersReducedMotion();
  const visualRef = useRef<HTMLDivElement>(null);
  const shapeARef = useRef<HTMLDivElement>(null);
  const shapeBRef = useRef<HTMLDivElement>(null);
  const parallaxFrame = useRef(0);

  const handleVisualMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (reduceMotion || event.pointerType === "touch") return;
    const el = visualRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const nx = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const ny = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    if (parallaxFrame.current) cancelAnimationFrame(parallaxFrame.current);
    parallaxFrame.current = requestAnimationFrame(() => {
      el.style.transform = `translate3d(${(nx * 9).toFixed(1)}px, ${(
        ny * 9
      ).toFixed(1)}px, 0)`;
      if (shapeARef.current) {
        shapeARef.current.style.transform = `translate3d(${(-nx * 18).toFixed(
          1
        )}px, ${(-ny * 18).toFixed(1)}px, 0)`;
      }
      if (shapeBRef.current) {
        shapeBRef.current.style.transform = `translate3d(${(nx * 22).toFixed(
          1
        )}px, ${(ny * 22).toFixed(1)}px, 0)`;
      }
    });
  };

  const handleVisualLeave = () => {
    if (parallaxFrame.current) cancelAnimationFrame(parallaxFrame.current);
    for (const ref of [visualRef, shapeARef, shapeBRef]) {
      if (ref.current) ref.current.style.transform = "translate3d(0, 0, 0)";
    }
  };

  return (
    <Stack
      sx={{
        position: "relative",
        // Breathing room under the fixed navbar, matching /animation-demo.
        pt: "clamp(48px, 8vw, 96px)",
      }}
    >
      {/* The page-wide animated background now lives in the app layout. */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
      <Grid
        container
        spacing={2}
        sx={{
          alignItems: "center",
        }}
      >
        <Grid item xs={12} md={6}>
          <div>
            <span className="anim-eyebrow anim-fade-up" style={delay(40)}>
              Software Engineer · 4+ yrs
            </span>

            <h1 className="anim-title">
              {headingWords.map((word, index) => (
                <Fragment key={`${word.text}-${index}`}>
                  {index > 0 && " "}
                  <span
                    className={`anim-word${word.accent ? " anim-accent" : ""}`}
                    style={delay(120 + index * 55)}
                  >
                    {word.text}
                  </span>
                </Fragment>
              ))}
            </h1>

            <div className="anim-roleline anim-fade-up" style={delay(700)}>
              <span>I build</span>
              <RotatingRole roles={HERO_ROLES} />
            </div>

            <p className="anim-lead anim-fade-up" style={delay(790)}>
              {subtitle}
            </p>

            <div className="anim-cta anim-fade-up" style={delay(880)}>
              <Link
                to="/contact"
                className="anim-btn anim-btn--solid"
                onClick={() => {
                  const fbq = addScriptDefault();
                  fbq("trackCustom", "contactMe", {
                    someKey: "contactMe",
                  });
                }}
              >
                <span>Contact me</span>
                <span className="anim-btn__arrow" aria-hidden="true">
                  →
                </span>
              </Link>
              <Link to="/portfolio" className="anim-btn">
                <span>View projects</span>
              </Link>
            </div>

            <div className="anim-meta anim-fade-up" style={delay(980)}>
              <span>
                <i className="anim-meta__dot anim-pulse" aria-hidden="true" />
                Available for opportunities
              </span>
              <span>
                {text}{" "}
                <a
                  className="anim-link"
                  href={url}
                  target={target || "_self"}
                  rel="noreferrer"
                >
                  {urlText}
                </a>
              </span>
            </div>

            <div className="anim-socials anim-fade-up" style={delay(1080)}>
              {layout.sidebar.map((item) => (
                <a
                  key={item.id}
                  className="anim-social"
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={item.name}
                  title={item.name}
                >
                  {item.icon}
                </a>
              ))}
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            ref={visualRef}
            onPointerMove={handleVisualMove}
            onPointerLeave={handleVisualLeave}
            sx={{
              display: "flex",
              flexDirection: "column",
              position: "relative",
              px: { xs: 0, md: 2, lg: 12 },
              transition: "transform 0.25s ease-out",
            }}
          >
            <Box
              ref={shapeARef}
              sx={{
                width: 200,
                height: 200,
                position: "absolute",
                top: "10%",
                zIndex: 1,
                aspectRatio: "1/1",
                transition: "transform 0.25s ease-out",
              }}
            >
              <Box
                className="anim-float"
                style={delay(300)}
                sx={{ width: "100%", height: "100%" }}
              >
                <RectangleShape color={theme.palette.primary.main} />
              </Box>
            </Box>
            <Box
              className="anim-visual-in"
              style={delay(240)}
              sx={{
                position: "relative",
                zIndex: 2,
                // Reserve the portrait's aspect ratio (banner.png is 443×379)
                // so the hero does not collapse and then jump while the image
                // is still loading.
                aspectRatio: "443 / 379",
                borderBottom: (theme) =>
                  `1px solid ${theme.palette.primary.main}`,
                display: "block",
                maxWidth: "100%",
              }}
            >
              <LazyLoadImage
                alt={"hero_img"}
                effect="blur"
                src={image}
                width={"100%"}
                height={"100%"}
              />
            </Box>

            <Box
              className="anim-fade-up"
              style={delay(700)}
              sx={{
                padding: "8px",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                color: (theme) => theme.palette.text.primary,

                margin: "0px auto",
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                top: -"1px",
                zIndex: 3,
                gap: "8px",
                fontWeight: 500,
              }}
            >
              <Box
                component={"span"}
                className="anim-pulse"
                style={{
                  content: "",
                  display: "inline-block",
                  aspectRatio: "1/1",
                  width: "16px",
                  // backgroundColor: (theme) => theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.main,
                  // border: (theme) => `1px solid ${theme.palette.divider}`,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              ></Box>
              {text}

              <Typography
                component={Link}
                to={url}
                target={target || "_self"}
                color="primary.main"
                className="anim-link"
              >
                {urlText}
              </Typography>
            </Box>
            {/* <Box
              sx={{
                ":after": {
                  content: '""',
                  // display: 'block',
                  //  backgroundImage: `url(${isDark ? dotLight : dotDark})`,
                  backgroundImage: `url(${dotDark})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  position: "absolute",
                  top: '40%',
                  left: '70%',
                  bottom: 30,
                  width: 100,
                  height: 100,
                  zIndex: 1

                },
              }}
            /> */}
            <Box
              ref={shapeBRef}
              sx={{
                position: "absolute",
                top: "40%",
                left: "70%",
                bottom: 30,
                width: 100,
                height: 100,
                zIndex: 1,
                transition: "transform 0.25s ease-out",
              }}
            >
              <Box
                className="anim-float"
                style={delay(520)}
                sx={{ width: "100%", height: "100%" }}
              >
                <DotedBox color={theme.palette.text.primary} />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <div className={styles.blockquoteWrapper}>
        <div className={styles.blockquote}>
          <Box
            // Not a heading: this is a pull-quote. It used to be an <h1>,
            // which put a second top-level heading on the page and broke the
            // document outline (Lighthouse: heading-order). Styled via the
            // class so the look is unchanged.
            component={"blockquote"}
            className={styles.quoteHeading}
            sx={{
              color: (theme) => theme.palette.primary.main,
              ":before": {
                content: '""',
                position: "absolute",
                width: 80,
                border: (theme) =>
                  `6px solid ${theme.palette.background.default}`,
                bottom: -3,
                left: 50,
                zIndex: 2,
              },

              ":after": {
                content: '""',
                position: "absolute",
                border: (theme) => `2px solid ${theme.palette.primary.main}`,
                borderRadius: "0 50px 0 0",
                width: "60px",
                height: " 60px",
                bottom: "-60px",
                left: "50px",
                borderBottom: "none",
                borderLeft: "none",
                zIndex: 3,
              },
            }}
          >
            {quoteText1}{" "}
            <Box
              component={"span"}
              color={(theme) => theme.palette.text.primary}
            >
              {quoteText2}
            </Box>{" "}
            {quoteText3}{" "}
            <Box
              component={"span"}
              color={(theme) => theme.palette.text.primary}
            >
              {" "}
              {quoteText4}
            </Box>
          </Box>
          <Box
            // Attribution for the pull-quote, not a section heading.
            component={"cite"}
            className={styles.quoteAuthor}
            sx={{
              color: (theme) => theme.palette.text.primary,
            }}
          >
            —{quoteBy}
            <br />
            {/* <em>Web Site Usability: A Designer's Guide</em> */}
          </Box>
        </div>
      </div>
      </Box>
    </Stack>
  );
}
